import axios from '../../axios';
import { toWireSafeInts } from '../../utils/wireSafeInts.js';

export default {
  namespaced: true,
  state: {
    loggedInUser: null,
  },
  getters: {
    loggedInUser: (state, getters) => state.loggedInUser,
    getGASessionId() {
      return $cookies.get('_ga_2KCXCK33ST') ? $cookies.get('_ga_2KCXCK33ST').split('.')[2] : '';
    },
    coSignInRole: (state) => (state.loggedInUser && state.loggedInUser.coSignIn ? state.loggedInUser.coSignInRole : null),
  },
  mutations: {
    setLoggedInUserToken(state, token) {
      if (token) $cookies.set('user_api_token', token, '3m', null, null, false);
      else $cookies.remove('user_api_token');
    },
    setLoggedInUser(state, data) {
      if (data) {
        // Strip the backend's int.MinValue "not set" sentinels before the contact reaches any component.
        // The profile forms post this whole object back and the WAF blocks 32-bit integer extremes in a
        // request - see utils/wireSafeInts.js.
        toWireSafeInts(data);

        state.loggedInUser = data.contact;
        state.loggedInUser.coSignIn = data.coSignIn;
        state.loggedInUser.coSignInRole = data.coSignInRole;
        state.loggedInUser.loggedInAsContactAdmin = data.loggedInAsContactAdmin;
        // for GTM and tracking - place userid and email_id in datalayer for GTM
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ email_id: `${data.contact.email}`, userid: `${data.contact.id}` });
      }
      else
      { state.loggedInUser = null; }
    },
  },
  actions: {
    // Fetch user data for currently logged in user (JWT token)
    fetchLoggedInUser({ commit, dispatch, getters }, data) {
      return new Promise((resolve, reject) => {
        if ($cookies.get('user_api_token')) {
          if (!getters.loggedInUser || (data && data.forceReload)) {
            // only call the api if the cookie exist and the loggedInUser state is null
            axios.get('/core/contacts/current', {
              headers: {
                Ga_Session_Id: getters.getGASessionId,
              },
            }).then((res) => {
              commit('setLoggedInUser', res.data);
              resolve(res.data);
            }).catch((error) => {
              if (error.response && error.response.status === 401) {
                // the user session expired so reset booking state
                commit('setLoggedInUserToken', null);
                commit('setLoggedInUser', null);
              }
            });
          }
          else resolve(getters.loggedInUser);
        }
        else { resolve(null); }
      });
    },
    // Authenticate user against email & password and save returned JWT token in a cookie
    logIn({ commit, dispatch, getters }, user) {
      return new Promise((resolve, reject) => {
        axios({
          method: 'POST',
          url: '/auth/token',
          auth: {
            username: user.email,
            password: user.password,
          },
          headers: {
            usertype: user.userType === 'leadbooker' ? 'contact' : 'cosignin',
          },
        }).then((res) => {
          if (res.status == 200) {
            commit('setLoggedInUserToken', res.data);
            // load user data for currently logged in user
            dispatch('fetchLoggedInUser').then((res) => {
              // set currentContactId
              commit('contacts/setCurrenctContactId', res.contact.id, { root: true });
              resolve(res);
            });
          }
        }).catch((error) => {
          reject(error);
        });
      });
    },
    logInByToken({ commit, dispatch, getters }, token) {
      return new Promise((resolve, reject) => {
        axios({
          method: 'POST',
          url: '/auth/token/bytoken',
          headers: {
            token,
          },
        }).then((res) => {
          if (res.status == 200) {
            commit('setLoggedInUserToken', res.data);
            // load user data for currently logged in user
            dispatch('fetchLoggedInUser').then((res) => {
              resolve(res);
            });
          }
        }).catch((error) => {
          reject(error);
        });
      });
    },

    // googleAuthResponse must contain "accessToken" and "idToken" from Google authorisation response
    logInByGoogleAuthResponse({ commit, dispatch, getters }, googleAuthResponse) {
      return new Promise((resolve, reject) => {
        axios.post('/auth/token/bygoogleauth', googleAuthResponse).then((res) => {
          if (res.status == 200) {
            commit('setLoggedInUserToken', res.data);
            // load user data for currently logged in user
            dispatch('fetchLoggedInUser').then((res) => {
              commit('contacts/setCurrenctContactId', res.contact.id, { root: true });
              resolve(res);
            });
          }
        }).catch((error) => {
          reject(error);
        });
      });
    },

    // fbAuthResponse must contain "accessToken" and "userID" from Facebook authorisation response
    logInByFBAuthResponse({ commit, dispatch, getters }, fbAuthResponse) {
      return new Promise((resolve, reject) => {
        axios.post('/auth/token/byfbauth', fbAuthResponse).then((res) => {
          if (res.status == 200) {
            commit('setLoggedInUserToken', res.data);
            // load user data for currently logged in user
            dispatch('fetchLoggedInUser').then((res) => {
              // set currentContactId
              commit('contacts/setCurrenctContactId', res.contact.id, { root: true });
              resolve(res);
            });
          }
        }).catch((error) => {
          reject(error);
        });
      });
    },

    // appleAuthResponse must contain "code" and "id_toke" from apple authorisation response
    logInByAppleAuthResponse({ commit, dispatch, getters }, appleAuthResponse) {
      return new Promise((resolve, reject) => {
        // console.log(appleAuthResponse);
        const authResponse = {
          code: appleAuthResponse.authorization.code,
          id_token: appleAuthResponse.authorization.id_token,
          firstname: appleAuthResponse.user ? appleAuthResponse.user.name.firstName : null,
          lastname: appleAuthResponse.user ? appleAuthResponse.user.name.lastName : null,
        };
        axios.post('/auth/token/byappleauth', authResponse).then((res) => {
          if (res.status == 200) {
            commit('setLoggedInUserToken', res.data);
            // load user data for currently logged in user
            dispatch('fetchLoggedInUser').then((res) => {
              // set currentContactId
              commit('contacts/setCurrenctContactId', res.contact.id, { root: true });
              resolve(res);
            });
          }
        }).catch((error) => {
          reject(error);
        });
      });
    },

    // Refresh existing token that is sent in the header with all requests if it exists
    refreshToken({ commit, dispatch, getters }) {
      if ($cookies.get('user_api_token')) {
        return new Promise((resolve, reject) => {
          axios.post('/auth/refresh').then((res) => {
            commit('setLoggedInUserToken', res.data);
          }).catch((error) => {
            reject(error);
          });
        });
      }
    },
    // Check if supplied email address exists in database
    checkIfEmailExists({ commit, dispatch, getters }, user) {
      return new Promise((resolve, reject) => {
        const controller = user.userType === 'leadbooker' ? 'contacts' : 'cosignins';
        // Email goes in a query parameter, not the path: an address ending in .com reads as a
        // request for a .com file to the WAF and gets blocked.
        axios.get(`/core/${controller}/exists?email=${encodeURIComponent(user.email)}`, {
        }).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    // Requests a password reset link that will be sent via email if the user exists in the database
    requestPasswordResetEmail({ commit, dispatch, getters }, user) {
      return new Promise((resolve, reject) => {
        const controller = user.userType === 'leadbooker' ? 'contacts' : 'cosignins';
        // As above — email in a query parameter rather than the URL path.
        axios.post(`/core/${controller}/passwordrecovery?email=${encodeURIComponent(user.email)}`, {
        }).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    // fetch email address by token
    fetchEmailFromToken({ commit, dispatch, getters }, user) {
      return new Promise((resolve, reject) => {
        const controller = user.userType === 'leadbooker' ? 'contacts' : 'cosignins';
        axios(
          {
            method: 'GET',
            url: `/core/${controller}/getemailfromtoken`,
            headers: {
              token: user.token,
            },
          },
        ).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    // fetch email address by token
    fetchBookingInfoFromTokenAndId({ commit, dispatch, getters }, data) {
      return new Promise((resolve, reject) => {
        const controller = 'contacts';
        axios(
          {
            method: 'GET',
            url: `/core/${controller}/fetchBookingInfoFromTokenAndId`,
            headers: {
              token: data.token,
              bookingid: data.bookingId,
            },
          },
        ).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    submitCompleteRegistration({ commit, dispatch, getters }, payload) {
      return new Promise((resolve, reject) => {
        const controller = 'contacts';
        axios(
          {
            method: 'POST',
            url: `/core/${controller}/complete-registration`,
            headers: {
              token: payload.token,
              bookingid: payload.bookingId,
            },
            data: payload,
          },
        ).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    changePassword({
      state, commit, dispatch, getters, rootGetters,
    }, data) {
      return new Promise((resolve, reject) => {
        axios.post('/core/contacts/changepassword', data).then((res) => resolve(res)).catch((error) => reject(error));
      });
    },
    // reset password
    resetPassword({ commit, dispatch, getters }, user) {
      return new Promise((resolve, reject) => {
        const controller = user.userType === 'leadbooker' ? 'contacts' : 'cosignins';
        // input is an encoded Base64 string in format newpassword<|>token
        const input = btoa(`${user.newPassword}<|>${user.token}`);
        axios(
          {
            method: 'POST',
            url: `/core/${controller}/passwordreset`,
            headers: {
              input,
            },
          },
        ).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    // update profile
    updateProfile({ commit, dispatch, getters }, user) {
      return new Promise((resolve, reject) => {
        // const controller = user.userType === 'leadbooker' ? 'contacts' : 'cosignins'; // new CoSignIns are actual contact objects - so we use main contacts controller
        axios.post(`/core/contacts/${user.id}/profile`, user).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    // update subscription preferences
    updateSubs({ commit, dispatch, getters }, user) {
      return new Promise((resolve, reject) => {
        const controller = user.userType === 'leadbooker' ? 'contacts' : 'guestlogins';
        axios.post(`/core/${controller}/${user.id}/subscriptions`, user).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    // update subscription preferences - emailsub one click (data = { contactId : 3, subscribed : true})
    updateSubsOneClick({ commit, dispatch, getters }, data) {
      return new Promise((resolve, reject) => {
        axios.post(`/core/contacts/${data.contactId}/subscriptions/oneclickemail/${data.subscribed}`).then((res) => {
          resolve(res);
        }).catch((error) => {
          reject(error);
        });
      });
    },
    // remove cookie and set state to null
    logOut({
      commit, dispatch, getters, rootGetters,
    }) {
      // Revoke the JWT server-side so a captured token can't be replayed after logout.
      // Uses fetch (not axios) to bypass the 401 interceptor, which would otherwise recurse
      // back into logOut if the revoke call itself ever returned 401.
      const token = $cookies.get('user_api_token');
      if (token) {
        try {
          fetch(`${process.env.VUE_APP_API}/auth/revoke`, {
            method: 'POST',
            headers: { Authorization: `bearer ${token}` },
            keepalive: true,
          }).catch(() => {});
        } catch (e) { /* swallow — logout must always proceed */ }
      }
      // console.log('logging out');
      commit('setLoggedInUserToken', null);
      commit('setLoggedInUser', null);
      $cookies.remove('user_api_token');
      // remove booking stuff
      commit('bookings/setCurrentBooking', null, { root: true });
      commit('sca/setVilla', null, { root: true });
      commit('sca/setUsersBookings', null, { root: true });
      $cookies.remove('current_booking_id');
      $cookies.remove('current_Wine_Villa_Booking_ItemId');
    },
    // input = { email* : string, firstname* : string, lastname* : string, password* : string, ga_ClientId : string }   (* mandatory fields, if not set BadRequest will be returned)
    // response = contacttoken
    createAccountAndLogIn({ commit, dispatch, getters }, contact) {
      return new Promise((resolve, reject) => {
        axios.post('/auth/createaccount_and_token', contact).then((res) => {
          if (res.status == 200) {
            commit('setLoggedInUserToken', res.data);
            // load user data for currently logged in user
            dispatch('fetchLoggedInUser').then((res) => {
              // set currentContactId
              commit('contacts/setCurrenctContactId', res.contact.id, { root: true });
              resolve(res);
            });
          }
        }).catch((err) => { reject(err); });
      });
    },
    preRegisterAsGuest({ commit, dispatch, getters }, email) {
      return new Promise((resolve, reject) => {
        axios.post('/auth/preregister', { email }).then((res) => {
          if (res.status == 200) {
            resolve(res);
          }
        }).catch((err) => { reject(err); });
      });
    },
  },
};
